<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

import AddCustomDomainDialog from './AddCustomDomainDialog.vue';
import DNSConfigurationDialog from './DNSConfigurationDialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  widget: {
    type: Object,
    required: true,
  },
  isFetchingStatus: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'updateCustomDomain',
  'refreshStatus',
  'sendCnameInstructions',
]);

const SSL_STATUS = {
  LIVE: ['active', 'staging_active'],
  PENDING: [
    'provisioned',
    'pending',
    'initializing',
    'pending_validation',
    'pending_deployment',
    'pending_issuance',
    'holding_deployment',
    'holding_validation',
    'pending_expiration',
    'pending_cleanup',
    'pending_deletion',
    'staging_deployment',
    'backup_issued',
  ],
  ERROR: [
    'blocked',
    'inactive',
    'moved',
    'expired',
    'deleted',
    'timed_out_initializing',
    'timed_out_validation',
    'timed_out_issuance',
    'timed_out_deployment',
    'timed_out_deletion',
    'deactivating',
  ],
};

const { t } = useI18n();
const { isOnChatwootCloud } = useAccount();

const addCustomDomainDialogRef = ref(null);
const dnsConfigurationDialogRef = ref(null);
const updatedDomainAddress = ref('');

const customDomainAddress = computed(() => props.widget?.custom_domain || '');

const sslSettings = computed(() => props.widget?.ssl_settings || {});
const verificationErrors = computed(
  () => sslSettings.value.verification_errors || ''
);

const isLive = computed(() =>
  SSL_STATUS.LIVE.includes(sslSettings.value.status)
);
const isPending = computed(() =>
  SSL_STATUS.PENDING.includes(sslSettings.value.status)
);
const isError = computed(() =>
  SSL_STATUS.ERROR.includes(sslSettings.value.status)
);

const statusText = computed(() => {
  if (isLive.value) return t('BOOKING_WIDGETS.CUSTOM_DOMAIN.STATUS.LIVE');
  if (isPending.value) return t('BOOKING_WIDGETS.CUSTOM_DOMAIN.STATUS.PENDING');
  if (isError.value) return t('BOOKING_WIDGETS.CUSTOM_DOMAIN.STATUS.ERROR');
  return '';
});

const statusColors = computed(() => {
  if (isLive.value)
    return { text: 'text-n-teal-11', bubble: 'outline-n-teal-6 bg-n-teal-9' };
  if (isError.value)
    return { text: 'text-n-ruby-11', bubble: 'outline-n-ruby-6 bg-n-ruby-9' };
  return { text: 'text-n-amber-11', bubble: 'outline-n-amber-6 bg-n-amber-9' };
});

const updateCustomDomain = customDomain => {
  emit('updateCustomDomain', customDomain);
  addCustomDomainDialogRef.value.dialogRef.close();
  if (customDomain) {
    updatedDomainAddress.value = customDomain;
    dnsConfigurationDialogRef.value.dialogRef.open();
  }
};

const closeDNSConfigurationDialog = () => {
  updatedDomainAddress.value = '';
  dnsConfigurationDialogRef.value.dialogRef.close();
};

const onClickRefreshSSLStatus = () => {
  emit('refreshStatus');
};

const onClickSend = email => {
  emit('sendCnameInstructions', email);
};
</script>

<template>
  <div class="space-y-3">
    <div>
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('BOOKING_WIDGETS.CUSTOM_DOMAIN.HEADER') }}
      </h3>
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('BOOKING_WIDGETS.CUSTOM_DOMAIN.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex w-full items-center justify-between gap-2">
      <div v-if="customDomainAddress" class="flex min-w-0 flex-col gap-1">
        <div class="flex h-8 w-full items-center gap-4">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('BOOKING_WIDGETS.CUSTOM_DOMAIN.LABEL') }}
          </label>
          <span class="truncate text-sm text-n-slate-12">
            {{ customDomainAddress }}
          </span>
        </div>
        <span
          v-if="!isLive && isOnChatwootCloud"
          class="text-xs text-n-slate-11"
        >
          {{ t('BOOKING_WIDGETS.CUSTOM_DOMAIN.STATUS_DESCRIPTION') }}
        </span>
      </div>
      <div class="flex items-center">
        <div v-if="customDomainAddress" class="flex items-center gap-3">
          <div
            v-if="statusText && isOnChatwootCloud"
            v-tooltip="verificationErrors"
            class="flex flex-shrink-0 items-center gap-3"
          >
            <span
              class="block size-1.5 flex-shrink-0 rounded-full outline outline-2"
              :class="statusColors.bubble"
            />
            <span
              :class="statusColors.text"
              class="text-sm font-medium leading-[16px]"
            >
              {{ statusText }}
            </span>
          </div>
          <div
            v-if="statusText && isOnChatwootCloud"
            class="h-3 w-px bg-n-weak"
          />
          <Button
            slate
            sm
            link
            :label="t('BOOKING_WIDGETS.CUSTOM_DOMAIN.EDIT_BUTTON')"
            class="flex-shrink-0 hover:!no-underline"
            @click="addCustomDomainDialogRef.dialogRef.open()"
          />
          <div v-if="isOnChatwootCloud" class="h-3 w-px bg-n-weak" />
          <Button
            v-if="isOnChatwootCloud"
            slate
            sm
            link
            icon="i-lucide-refresh-ccw"
            :class="isFetchingStatus && 'animate-spin'"
            @click="onClickRefreshSSLStatus"
          />
        </div>
        <Button
          v-else
          :label="t('BOOKING_WIDGETS.CUSTOM_DOMAIN.ADD_BUTTON')"
          color="slate"
          sm
          @click="addCustomDomainDialogRef.dialogRef.open()"
        />
      </div>
    </div>

    <AddCustomDomainDialog
      ref="addCustomDomainDialogRef"
      :mode="customDomainAddress ? 'edit' : 'add'"
      :custom-domain="customDomainAddress"
      @add-custom-domain="updateCustomDomain"
    />
    <DNSConfigurationDialog
      ref="dnsConfigurationDialogRef"
      :custom-domain="updatedDomainAddress || customDomainAddress"
      @close="closeDNSConfigurationDialog"
      @send="onClickSend"
    />
  </div>
</template>
